'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';

import Filter from '@/components/Filter/Filter';
import { EFilterActions, useFilter } from '@/components/Filter/useFilter';
import PageTitle from '@/features/contentful/components/PageTitle';
import InsightCard from './InsightsCard';
import { usePagination } from '@/components/Pagination/usePagination';
import Pagination from '@/components/Pagination/Pagination';
import ContentLayout from '@/layouts/ContentLayout';
import { useSettingsContext } from '@/contexts/SettingsContext';

import {
    TypeInsightsWithAllLocalesAndWithoutUnresolvableLinksResponse,
    TypeSimplePageLayoutWithoutUnresolvableLinksResponse,
} from '../../_generated';
import capitalizeWords from '@/utils/capitalizeWords';
import { getLocalizedLink } from '@/utils/getLocalizedLink';
import { getLocalizedContent } from '@/utils/getLocalizedContent';

type TInsightsGridProps = {
    items: TypeInsightsWithAllLocalesAndWithoutUnresolvableLinksResponse[];
    total: number;
    skip: number;
    limit: number;
    pageIdx: number;
    filterKeys: string[];
    allFilters: any[];
    pageLayout: TypeSimplePageLayoutWithoutUnresolvableLinksResponse;
};

const InsightsGrid = ({
    items: initItems,
    total: initTotal,
    limit,
    pageIdx,
    filterKeys,
    allFilters,
    pageLayout,
}: TInsightsGridProps) => {
    const livePost = useContentfulLiveUpdates({ total: initTotal, items: initItems });
    const { items, total } = livePost;
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const { push } = useRouter();
    const { getLabel, lang } = useSettingsContext();
    const FILTERS_LABEL = getLabel('label', 'filters');
    const APPLY_LABEL = getLabel('label', 'apply');
    const CLEAR_ALL_LABEL = getLabel('label', 'clearAll');
    const BY_DIVISION_LABEL = getLabel('label', 'byDivision');
    const ALL_POSTS_TAGGED_LABEL = getLabel('label', 'allPostsTagged');
    const CLEAR_TAG_LABEL = getLabel('label', 'clearTag');

    const handleFilterChange = (
        updatedFilters: Record<string, string | string[]>,
        searchParams: URLSearchParams,
        action: EFilterActions,
    ) => {
        if (action === EFilterActions.Clear) {
            return;
        }
        searchParams.set('page', '1');
        push(`${pathname}?${searchParams.toString()}`);
    };

    const { appliedFilters, selectedFilters, updateFilters } = useFilter(
        {},
        filterKeys,
        handleFilterChange,
    );

    const appliedFiltersCount = useMemo(
        () =>
            Object.values(appliedFilters).reduce((acc, item) => {
                return acc + (Array.isArray(item) ? item.length : item ? 1 : 0);
            }, 0),
        [appliedFilters],
    );

    const handlePageChange = (pageIdx: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', (pageIdx + 1).toString());
        push(`${pathname}?${params.toString()}`);
    };

    const { handlePageOpen } = usePagination({
        initialPageIdx: pageIdx,
        totalItems: total,
        pageSize: limit,
        handlePageChange,
    });

    const handleFilterClick = (key: string) => (value: string) => {
        const params = new URLSearchParams(searchParams);
        const isFilterSelected = params.has(key, value.toLowerCase());
        params.delete('page');
        if (isFilterSelected) {
            params.delete(key);
            push(`${pathname}?${params.toString()}`);
        } else {
            params.set(key, value.toLowerCase());
            push(`${pathname}?${params.toString()}`);
        }
    };
    const currentTag = searchParams.get('tag');
    allFilters = allFilters.map((filters) => {
        return { ...filters, label: BY_DIVISION_LABEL };
    });

    return (
        <ContentLayout>
            <PageTitle
                heroComponent={pageLayout?.fields?.heroComponent}
                className="tw-mt-12 tw-mb-8"
            />
            <Filter
                allFilters={allFilters}
                selectedFilters={selectedFilters}
                appliedFiltersCount={appliedFiltersCount}
                updateFilters={updateFilters}
                filterBtnTitle={FILTERS_LABEL}
                applyBtnText={APPLY_LABEL}
                clearBtnText={CLEAR_ALL_LABEL}
            >
                {currentTag && (
                    <span className="tw-mr-auto tw-font-gotham-book tw-text-textBlue tw-leading-[19px]">
                        {ALL_POSTS_TAGGED_LABEL}
                        <span className="tw-font-gotham-medium">
                            ‘{capitalizeWords(currentTag)}’
                        </span>
                        <Link
                            href={getLocalizedLink('/insights', lang)}
                            className="tw-text-pink1 tw-underline-offset-4"
                        >
                            {CLEAR_TAG_LABEL}
                        </Link>
                    </span>
                )}
            </Filter>

            <div className="tw-grid tw-grid-cols-1 smd:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-3 tw-gap-5 tw-mt-5">
                {items.map((item) => (
                    <InsightCard
                        key={item.sys.id}
                        id={item.sys.id}
                        description={getLocalizedContent(item.fields.title, lang)}
                        uri={getLocalizedContent(item.fields.slug, lang)}
                        category={getLocalizedContent(item.fields?.division, lang)}
                        image={
                            getLocalizedContent(
                                getLocalizedContent(item.fields.heroimage, lang).fields.file,
                                lang,
                            ).url
                                ? `https:${getLocalizedContent(getLocalizedContent(item.fields.heroimage, lang).fields.file, lang).url}?fm=webp`
                                : undefined
                        }
                        imageAlt={
                            getLocalizedContent(
                                getLocalizedContent(item.fields.heroimage, lang).fields.file,
                                lang,
                            ).title
                        }
                        onCategoryClick={handleFilterClick('categories')}
                    />
                ))}
            </div>

            {total ? (
                <Pagination
                    className="tw-text-center tw-mt-8 tw-mb-20"
                    handlePageOpen={handlePageOpen}
                    totalPages={Math.ceil(total / limit)}
                    currentPageIdx={pageIdx}
                />
            ) : null}
        </ContentLayout>
    );
};

export default InsightsGrid;
